import { useState, useMemo, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader2, UserRound } from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Deterministic colour from a string. Returns a Tailwind-compatible
 * `bg-*` / `text-*` pair for the avatar initials circle.
 */
const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-rose-100', text: 'text-rose-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  { bg: 'bg-orange-100', text: 'text-orange-700' },
  { bg: 'bg-pink-100', text: 'text-pink-700' },
  { bg: 'bg-teal-100', text: 'text-teal-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
];

function getAvatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(firstName, lastName) {
  const first = (firstName || '')[0] || '';
  const last = (lastName || '')[0] || '';
  return (first + last).toUpperCase() || '?';
}

// ---------------------------------------------------------------------------
// Row animation
// ---------------------------------------------------------------------------
const ROW_VARIANTS = {
  hidden: { opacity: 0, y: 8 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: Math.min(i * 0.03, 0.3), duration: 0.25 },
  }),
};

// ---------------------------------------------------------------------------
// KioskNameSearch
// ---------------------------------------------------------------------------
// Search and select an employee by name.
//
// Props:
//   employees       (array)    - [{ id, display_name, first_name, last_name, avatar_url, department }]
//   onSelect        (function) - called with (user, clockedIn, 'search')
//   onGetOnSiteUsers (function) - async, returns list of on-site users
// ---------------------------------------------------------------------------

export default function KioskNameSearch({
  employees = [],
  onSelect,
  onGetOnSiteUsers,
}) {
  const [query, setQuery] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  // -------------------------------------------------------------------------
  // Filter employees based on the search query
  // -------------------------------------------------------------------------
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;

    return employees.filter((emp) => {
      const display = (emp.display_name || '').toLowerCase();
      const first = (emp.first_name || '').toLowerCase();
      const last = (emp.last_name || '').toLowerCase();
      const full = `${first} ${last}`;
      return (
        display.includes(q) ||
        first.includes(q) ||
        last.includes(q) ||
        full.includes(q)
      );
    });
  }, [employees, query]);

  // -------------------------------------------------------------------------
  // Handle selecting an employee
  // -------------------------------------------------------------------------
  const handleSelect = useCallback(
    async (user) => {
      if (loadingId) return;

      setLoadingId(user.id);

      try {
        // Check whether this user is currently clocked in and on break
        let clockedIn = false;
        let onBreak = false;
        let activeBreak = null;
        let userData = user;

        if (onGetOnSiteUsers) {
          const onSiteUsers = await onGetOnSiteUsers();
          const match = onSiteUsers.find(
            (u) => u.id === user.id || u.user_id === user.id,
          );
          if (match) {
            clockedIn = true;
            onBreak = match.is_on_break || false;
            activeBreak = match.active_break || null;
            userData = { ...user, clock_in_time: match.check_in_time || null };
          }
        }

        onSelect?.(userData, clockedIn, 'search', onBreak, activeBreak);
      } catch {
        // If the on-site check fails, default to not clocked in
        onSelect?.(user, false, 'search');
      } finally {
        setLoadingId(null);
      }
    },
    [loadingId, onGetOnSiteUsers, onSelect],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="flex flex-col gap-5 w-full max-w-xl mx-auto">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for your name..."
          className="w-full h-14 pl-13 pr-4 text-xl rounded-xl border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-shadow"
          autoFocus
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* Results list */}
      <div className="overflow-y-auto max-h-[calc(100vh-340px)] rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100">
        <AnimatePresence mode="popLayout">
          {filtered.length === 0 && (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400"
            >
              <UserRound className="w-10 h-10" />
              <p className="text-xl font-medium">No results found</p>
              {query && (
                <p className="text-base">
                  Try a different spelling or name
                </p>
              )}
            </motion.div>
          )}

          {filtered.map((emp, index) => {
            const color = getAvatarColor(
              `${emp.first_name || ''}${emp.last_name || ''}`,
            );
            const initials = getInitials(emp.first_name, emp.last_name);
            const displayName =
              emp.display_name ||
              `${emp.first_name || ''} ${emp.last_name || ''}`.trim() ||
              'Unknown';
            const isLoading = loadingId === emp.id;

            return (
              <motion.button
                key={emp.id}
                type="button"
                custom={index}
                variants={ROW_VARIANTS}
                initial="hidden"
                animate="visible"
                exit="hidden"
                layout
                className="w-full flex items-center gap-4 px-5 h-16 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors disabled:opacity-60"
                whileTap={{ scale: 0.95 }}
                onClick={() => handleSelect(emp)}
                disabled={!!loadingId}
                aria-label={`Select ${displayName}`}
              >
                {/* Avatar */}
                {emp.avatar_url ? (
                  <img
                    src={emp.avatar_url}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${color.bg} ${color.text}`}
                  >
                    {initials}
                  </div>
                )}

                {/* Name & department */}
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-medium text-slate-800 truncate leading-tight">
                    {displayName}
                  </p>
                  {emp.department && (
                    <p className="text-sm text-slate-500 truncate leading-tight">
                      {emp.department}
                    </p>
                  )}
                </div>

                {/* Loading indicator */}
                {isLoading && (
                  <Loader2 className="w-5 h-5 text-slate-400 animate-spin flex-shrink-0" />
                )}
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
